import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/shared/ui/button'
import { TableHead } from '@/shared/ui/table'
import { pageCount, sortDirection, toggledSort, type Listing } from '../listing'

/**
 * The parts every administration listing has: a heading that sorts, and a way
 * through the pages.
 *
 * Presentational only. The listing itself lives in the address, so each page
 * reads and writes its own `?page=`, `?search=` and `?sort=` and hands the
 * result down -- which is what makes an administrator's search something they
 * can send to a colleague.
 */

/**
 * A column heading that sorts by its column.
 *
 * `aria-sort` is on the heading rather than an icon alone, because that is the
 * only part of this a screen reader can read: an arrow is a picture of the
 * answer and the attribute is the answer.
 */
export function SortHead({
  field,
  label,
  listing,
  onSort,
  className,
}: {
  field: string
  label: string
  listing: Listing
  onSort: (sort: string) => void
  className?: string
}) {
  const direction = sortDirection(listing.sort, field)

  return (
    <TableHead
      className={className}
      aria-sort={
        direction === null ? 'none' : direction === 'asc' ? 'ascending' : 'descending'
      }
    >
      <button
        type="button"
        className="hover:text-foreground -mx-1 flex items-center gap-1 rounded px-1"
        onClick={() => {
          onSort(toggledSort(listing.sort, field))
        }}
      >
        {label}
        {direction === 'asc' && <ArrowUp aria-hidden className="size-3" />}
        {direction === 'desc' && <ArrowDown aria-hidden className="size-3" />}
      </button>
    </TableHead>
  )
}

/**
 * Which page is being read, and the way to another.
 *
 * The count comes from the endpoint and for workspaces it is an approximation
 * on a large instance, so it is reported and never used to decide anything --
 * the buttons turn on which page this is, not on how many rows the server
 * thinks there are.
 */
export function Pages({
  count,
  listing,
  onPage,
}: {
  count: number
  listing: Listing
  onPage: (page: number) => void
}) {
  const { t } = useTranslation()
  const pages = pageCount(count)

  return (
    <div className="flex items-center justify-between gap-4 py-3">
      <p className="text-muted-foreground text-sm">{t('admin.total', { count })}</p>
      <div className="flex items-center gap-2">
        <p className="text-muted-foreground text-sm">
          {t('admin.page', { page: listing.page, pages })}
        </p>
        <Button
          variant="outline"
          size="icon"
          className="size-8 on-surface"
          aria-label={t('admin.previousPage')}
          disabled={listing.page <= 1}
          onClick={() => {
            onPage(listing.page - 1)
          }}
        >
          <ChevronLeft />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="size-8 on-surface"
          aria-label={t('admin.nextPage')}
          disabled={listing.page >= pages}
          onClick={() => {
            onPage(listing.page + 1)
          }}
        >
          <ChevronRight />
        </Button>
      </div>
    </div>
  )
}
